import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import type { Media } from "@zivin-labs/site-content";
import { Figure } from "./Figure";

interface LetterDialogProps {
  open: boolean;
  onClose: () => void;
  kicker: string;
  headline: string;
  paragraphs: string[];
  name: string;
  role: string;
  photo?: Media | null;
  photoLabel?: string;
}

/**
 * Full-text modal for a leadership letter too long to print in place.
 *
 * Follows the gallery lightbox's conventions — Escape and a backdrop click both
 * close — and adds what a text dialog needs that an image one does not: focus
 * moves to the panel on open and returns to whatever opened it on close, and
 * the page behind is locked so the backdrop does not scroll under the reader.
 *
 * Rendered through a portal to <body>. Mounted in place it inherits whatever
 * the surrounding section paints: on the home page it sits inside
 * `.section-navy`, whose white heading colour landed on this panel's off-white
 * ground and made the headline and name invisible. A portal also keeps the
 * fixed overlay clear of any ancestor transform or overflow.
 */
export function LetterDialog({
  open,
  onClose,
  kicker,
  headline,
  paragraphs,
  name,
  role,
  photo,
  photoLabel,
}: LetterDialogProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    returnFocusRef.current = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);

    // Lock the page behind the dialog, restoring whatever overflow was set.
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
      returnFocusRef.current?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="letter-dialog" role="dialog" aria-modal="true" aria-label={`${kicker} — full text`} onClick={onClose}>
      <div
        className="letter-dialog-panel"
        ref={panelRef}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="letter-dialog-close" onClick={onClose} aria-label="Close">
          ×
        </button>

        <div className="letter-dialog-head">
          {photo && (
            <Figure
              media={photo}
              fallbackLabel={photoLabel ?? name}
              aspect="4/5"
              className="letter-dialog-photo"
              sizes="88px"
            />
          )}
          <div>
            <div className="kicker">{kicker}</div>
            <div className="letter-dialog-name">{name}</div>
            <div className="letter-dialog-role">{role}</div>
          </div>
        </div>

        <h2 className="letter-dialog-headline">{headline}</h2>
        <div className="letter-dialog-body">
          {paragraphs.map((t, i) => (
            <p key={i}>{t}</p>
          ))}
        </div>
        <div className="letter-dialog-sign">
          {name}
          <span>{role}</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
